import { useQuery } from '@apollo/client/react'
import {
  Button,
  DataTable,
  ErrorState,
  FilterBar,
  LoadingState,
  Notice,
  Section,
  Select,
  StatTile,
  TextInput,
  FOCUS_RING,
  usePageTitle,
  useTranslate,
} from '@athena/ui'
import type { TranslationKey } from '@athena/i18n'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useState } from 'react'
import {
  AiProviderComparisonDocument,
  AiGenerationDiagnosticsDocument,
  type AiComparisonFieldsFragment,
  type AiGenerationDiagnosticsQuery,
} from '../graphql/generated/operations.js'
import { AIConfiguration } from '../features/ai-analytics/AIConfiguration.js'
import { AITrend } from '../features/ai-analytics/AITrend.js'
import { ListMessage, PageControls } from '../features/management/ManagementSupport.js'
import { useFocusFirstInvalid } from '../lib/validation.js'
import {
  analyticsDates,
  analyticsFilter,
  aiFormatting,
  aiStatus,
  providerName,
  validDay,
  type AIAnalyticsSearch,
} from '../lib/ai-analytics.js'
import { useLanguage } from '@athena/ui'
import { formatDateTime } from '../lib/display.js'

type Summary = AiComparisonFieldsFragment
type Diagnostic = AiGenerationDiagnosticsQuery['adminAIGenerationDiagnostics']['records'][number]
export function AIProviderComparisonScreen() {
  const t = useTranslate()
  const language = useLanguage()
  usePageTitle(t('aiAnalytics.title'))
  const search = useSearch({ from: '/$language/ai' })
  const navigate = useNavigate({ from: '/$language/ai' })
  const change = (next: AIAnalyticsSearch) => {
    void navigate({ search: next })
  }
  const filter = analyticsFilter(search)
  const invalidRange =
    Date.parse(filter.to) <= Date.parse(filter.from) ||
    Date.parse(filter.to) - Date.parse(filter.from) > 366 * 86400000
  const { data, loading, error, refetch } = useQuery(AiProviderComparisonDocument, {
    variables: { filter },
    skip: invalidRange,
  })
  const diagnostics = useQuery(AiGenerationDiagnosticsDocument, {
    variables: { filter, offset: ((search.page ?? 1) - 1) * 25, sort: search.sort ?? 'NEWEST' },
    skip: invalidRange,
  })
  const analytics = data?.adminAIProviderAnalytics
  const records = diagnostics.data?.adminAIGenerationDiagnostics
  const format = aiFormatting(language, t)
  return (
    <>
      <Section id="ai-providers" level={1} title={t('aiAnalytics.title')}>
        <p className="max-w-prose text-body-sm text-text-secondary">{t('aiAnalytics.body')}</p>
        <AnalyticsFilters key={JSON.stringify(search)} search={search} change={change} />
        {invalidRange ? <Notice tone="error" title={t('aiAnalytics.invalidDates')} /> : null}
        {loading ? <LoadingState>{t('common.loading')}</LoadingState> : null}
        {error ? (
          <div>
            <ErrorState>{t('admin.loadError')}</ErrorState>
            <Button variant="secondary" onClick={() => void refetch()}>
              {t('common.tryAgain')}
            </Button>
          </div>
        ) : null}
        {analytics && analytics.overview.requests === 0 ? (
          <ListMessage>{t('aiAnalytics.noData')}</ListMessage>
        ) : null}
        {analytics && analytics.overview.requests > 0 ? (
          <>
            <Section id="ai-overview" title={t('aiAnalytics.overview')}>
              <div className="grid gap-4 sm:grid-cols-2 desktop:grid-cols-4">
                <StatTile
                  label={t('aiAnalytics.requests')}
                  value={format.number(analytics.overview.requests)}
                />
                {['openai', 'gemini'].map((provider) => {
                  const row = analytics.providers.find((r) => r.provider === provider)
                  return (
                    <StatTile
                      key={provider}
                      label={providerName(provider)}
                      value={format.number(row?.requests ?? 0)}
                      detail={format.rate(row?.requests ?? 0, analytics.overview.requests)}
                    />
                  )
                })}
                <StatTile
                  label={t('aiAnalytics.successRate')}
                  value={format.rate(analytics.overview.succeeded, analytics.overview.requests)}
                />
              </div>
              <p className="text-body-sm text-text-secondary">
                {t('aiAnalytics.costCoverage', [
                  analytics.overview.costSamples,
                  analytics.overview.requests,
                ])}
              </p>
              <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2 desktop:grid-cols-4">
                {(
                  [
                    [
                      'aiAnalytics.averageLatency',
                      format.latency(analytics.overview.averageLatencyMs),
                    ],
                    ['aiAnalytics.medianLatency', format.latency(analytics.overview.p50LatencyMs)],
                    ['aiAnalytics.p95Latency', format.latency(analytics.overview.p95LatencyMs)],
                    ['aiAnalytics.cost', format.money(analytics.overview.totalCostUsd)],
                    [
                      filter.operation === 'PRACTICE_EXTRACTION'
                        ? 'aiAnalytics.extractionCostPerSuccess'
                        : 'aiAnalytics.costPerSuccess',
                      format.money(analytics.overview.averageCostPerSuccessUsd),
                    ],
                    ['aiAnalytics.inputTokens', format.number(analytics.overview.inputTokens)],
                    ['aiAnalytics.outputTokens', format.number(analytics.overview.outputTokens)],
                    [
                      'aiAnalytics.pipelineLatency',
                      format.latency(analytics.overview.averagePipelineMs),
                    ],
                  ] as const
                ).map(([key, value]) => (
                  <div key={key}>
                    <dt className="text-caption text-text-secondary">{t(key)}</dt>
                    <dd className="mt-1 text-h4 font-semibold tabular-nums">{value}</dd>
                  </div>
                ))}
              </dl>
            </Section>
            <Section id="ai-comparison" title={t('aiAnalytics.comparison')}>
              <p className="max-w-prose text-body-sm text-text-secondary">
                {t('aiAnalytics.methodology')}
              </p>
              <SummaryTable
                operation={filter.operation ?? 'PRACTICE_GENERATION'}
                rows={['openai', 'gemini'].map(
                  (p) => analytics.providers.find((r) => r.provider === p) ?? null,
                )}
              />
            </Section>
            <Section id="ai-models" title={t('aiAnalytics.models')}>
              <p className="max-w-prose text-body-sm text-text-secondary">
                {t('aiAnalytics.modelNote')}
              </p>
              <DataTable
                caption={t('aiAnalytics.models')}
                rows={analytics.models}
                rowKey={(r) => `${r.provider}-${r.model}`}
                columns={[
                  {
                    id: 'model',
                    header: t('aiAnalytics.model'),
                    cell: (r) => (
                      <span className="break-words">
                        {providerName(r.provider)} · {r.model}
                      </span>
                    ),
                  },
                  {
                    id: 'requests',
                    header: t('aiAnalytics.requests'),
                    cell: (r) => format.number(r.requests),
                  },
                  {
                    id: 'success',
                    header: t('aiAnalytics.successRate'),
                    cell: (r) => format.rate(r.succeeded, r.requests),
                  },
                  {
                    id: 'p50',
                    header: t('aiAnalytics.medianLatency'),
                    cell: (r) => format.latency(r.p50LatencyMs),
                  },
                  {
                    id: 'p95',
                    header: t('aiAnalytics.p95Latency'),
                    cell: (r) => format.latency(r.p95LatencyMs),
                  },
                  {
                    id: 'cost',
                    header: t('aiAnalytics.cost'),
                    cell: (r) => (
                      <span>
                        {format.money(r.totalCostUsd)}
                        <span className="block text-caption text-text-secondary">
                          {t('aiAnalytics.samples', [r.costSamples, r.requests])}
                        </span>
                      </span>
                    ),
                  },
                ]}
              />
            </Section>
            {filter.operation === 'PRACTICE_GENERATION' ? (
              <Section id="ai-quality" title={t('aiAnalytics.quality')}>
                <p className="max-w-prose text-body-sm text-text-secondary">
                  {t('aiAnalytics.qualityNote')}
                </p>
                <SummaryTable
                  rows={['openai', 'gemini'].map(
                    (p) => analytics.providers.find((r) => r.provider === p) ?? null,
                  )}
                  quality
                />
              </Section>
            ) : null}
            <Section id="ai-reliability" title={t('aiAnalytics.reliability')}>
              <DataTable
                caption={t('aiAnalytics.reliability')}
                rows={analytics.reliability}
                rowKey={(r) => r.category}
                columns={[
                  {
                    id: 'category',
                    header: t('aiAnalytics.status'),
                    cell: (r) => aiStatus(t, r.category),
                  },
                  {
                    id: 'count',
                    header: t('aiAnalytics.requests'),
                    cell: (r) => format.number(r.count),
                  },
                ]}
              />
            </Section>
            <Section id="ai-trends" title={t('aiAnalytics.trends')}>
              <div className="grid min-w-0 gap-6 desktop:grid-cols-2">
                {(
                  [
                    ['requests', 'aiAnalytics.requests'],
                    ['latency', 'aiAnalytics.averageLatency'],
                    ['cost', 'aiAnalytics.cost'],
                    ['success', 'aiAnalytics.successRate'],
                    ['tokens', 'aiAnalytics.tokensPerRequest'],
                    ['editing', 'aiAnalytics.editRate'],
                  ] as const
                )
                  .filter(
                    ([metric]) =>
                      metric !== 'editing' || filter.operation === 'PRACTICE_GENERATION',
                  )
                  .map(([metric, key]) => (
                    <AITrend
                      key={metric}
                      metric={metric}
                      title={t(key)}
                      daily={analytics.daily}
                      language={language}
                    />
                  ))}
              </div>
            </Section>
          </>
        ) : null}
      </Section>
      <Section id="ai-diagnostics" title={t('aiAnalytics.diagnostics')}>
        <Select
          label={t('aiAnalytics.sort')}
          value={search.sort ?? 'NEWEST'}
          onValueChange={(sort) =>
            change({ ...search, sort: sort as AIAnalyticsSearch['sort'] & string, page: 1 })
          }
          options={[
            { value: 'NEWEST', label: t('aiAnalytics.newest') },
            { value: 'OLDEST', label: t('aiAnalytics.oldest') },
            { value: 'DURATION_DESC', label: t('aiAnalytics.slowest') },
            { value: 'COST_DESC', label: t('aiAnalytics.highestCost') },
          ]}
        />
        {diagnostics.loading ? <LoadingState>{t('common.loading')}</LoadingState> : null}
        {diagnostics.error ? <ErrorState>{t('admin.loadError')}</ErrorState> : null}
        {records?.records.length ? (
          <DataTable
            caption={t('aiAnalytics.diagnostics')}
            rows={records.records}
            rowKey={(r) => r.id}
            busy={diagnostics.loading}
            columns={[
              {
                id: 'date',
                header: t('aiAnalytics.timestamp'),
                cell: (r) => (
                  <div>
                    <span>{formatDateTime(r.createdAt, language)}</span>
                    <DiagnosticDetails row={r} />
                  </div>
                ),
              },
              {
                id: 'provider',
                header: t('aiAnalytics.provider'),
                cell: (r) => providerName(r.originalProvider),
              },
              {
                id: 'model',
                header: t('aiAnalytics.model'),
                cell: (r) => (
                  <span className="break-words">
                    {r.attempts.map((a) => a.model).join(', ') || t('aiAnalytics.unavailable')}
                  </span>
                ),
              },
              {
                id: 'operation',
                header: t('aiAnalytics.operation'),
                cell: (r) =>
                  t(
                    r.operation === 'PRACTICE_EXTRACTION'
                      ? 'aiAnalytics.extraction'
                      : 'aiAnalytics.generation',
                  ),
                priority: 'secondary',
              },
              { id: 'status', header: t('aiAnalytics.status'), cell: (r) => aiStatus(t, r.status) },
              {
                id: 'duration',
                header: t('aiAnalytics.duration'),
                cell: (r) => format.latency(r.durationMs),
              },
              {
                id: 'input',
                header: t('aiAnalytics.inputTokens'),
                cell: (r) => format.number(sumAttempts(r, 'inputTokens')),
                priority: 'secondary',
              },
              {
                id: 'output',
                header: t('aiAnalytics.outputTokens'),
                cell: (r) => format.number(sumAttempts(r, 'outputTokens')),
                priority: 'secondary',
              },
              {
                id: 'cost',
                header: t('aiAnalytics.cost'),
                cell: (r) => format.money(sumAttempts(r, 'totalCostUsd')),
              },
              {
                id: 'questions',
                header: t('aiAnalytics.questionCount'),
                cell: (r) => format.number(r.generatedQuestionCount),
                priority: 'secondary',
              },
              {
                id: 'error',
                header: t('aiAnalytics.errorCategory'),
                cell: (r) => aiStatus(t, r.errorCategory),
                priority: 'secondary',
              },
            ]}
          />
        ) : records && !diagnostics.loading ? (
          <ListMessage>{t('aiAnalytics.noData')}</ListMessage>
        ) : null}
        {records ? (
          <PageControls
            page={search.page ?? 1}
            total={records.total}
            limit={25}
            disabled={diagnostics.loading}
            onChange={(page) => change({ ...search, page })}
          />
        ) : null}
      </Section>
      <AIConfiguration />
    </>
  )
}
function sumAttempts(row: Diagnostic, key: 'inputTokens' | 'outputTokens' | 'totalCostUsd') {
  const values = row.attempts.map((a) => a[key])
  return values.length && values.every((v) => v != null)
    ? values.reduce<number>((sum, v) => sum + (v ?? 0), 0)
    : null
}
function SummaryTable({
  rows,
  total = false,
  quality = false,
  operation = 'PRACTICE_GENERATION',
}: {
  rows: (Summary | null)[]
  total?: boolean
  quality?: boolean
  operation?: 'PRACTICE_GENERATION' | 'PRACTICE_EXTRACTION'
}) {
  const t = useTranslate()
  const language = useLanguage()
  const f = aiFormatting(language, t)
  type Metric = {
    key: TranslationKey
    value: (r: Summary) => string
    samples: (r: Summary) => number
  }
  const count = (key: TranslationKey, field: keyof Summary): Metric => ({
    key,
    value: (r) => f.number(r[field] as number | null),
    samples: (r) => r.requests,
  })
  const metrics: Metric[] = quality
    ? [
        ...(
          [
            ['aiAnalytics.missingAnswers', 'missingAnswers'],
            ['aiAnalytics.missingExplanations', 'missingExplanations'],
            ['aiAnalytics.invalidReferences', 'invalidAnswerReferences'],
            ['aiAnalytics.malformedQuestions', 'malformedQuestions'],
            ['aiAnalytics.duplicates', 'duplicateQuestionCandidates'],
          ] as const
        ).map(([key, field]) => ({
          ...count(key, field),
          samples: (r: Summary) => r.qualitySamples,
        })),
        ...(
          [
            ['aiAnalytics.edited', 'questionsEdited'],
            ['aiAnalytics.deleted', 'questionsDeleted'],
            ['aiAnalytics.added', 'questionsAdded'],
            ['aiAnalytics.explanationsEdited', 'explanationsEdited'],
            ['aiAnalytics.optionsEdited', 'optionsEdited'],
          ] as const
        ).map(([key, field]) => ({
          ...count(key, field),
          samples: (r: Summary) => r.editingSamples,
        })),
      ]
    : [
        count('aiAnalytics.requests', 'requests'),
        {
          key: 'aiAnalytics.successRate',
          value: (r) => f.rate(r.succeeded, r.requests),
          samples: (r) => r.requests,
        },
        ...(
          [
            ['aiAnalytics.averageLatency', 'averageLatencyMs'],
            ['aiAnalytics.medianLatency', 'p50LatencyMs'],
            ['aiAnalytics.p95Latency', 'p95LatencyMs'],
          ] as const
        ).map(([key, field]) => ({
          key,
          value: (r: Summary) => f.latency(r[field]),
          samples: (r: Summary) => r.latencySamples,
        })),
        ...(
          [
            ['aiAnalytics.inputTokens', 'inputTokens'],
            ['aiAnalytics.outputTokens', 'outputTokens'],
            ['aiAnalytics.cachedTokens', 'cachedInputTokens'],
            ['aiAnalytics.reasoningTokens', 'reasoningTokens'],
          ] as const
        ).map(([key, field]) => ({
          ...count(key, field),
          samples: (r: Summary) =>
            field === 'inputTokens'
              ? r.inputTokenSamples
              : field === 'outputTokens'
                ? r.outputTokenSamples
                : field === 'cachedInputTokens'
                  ? r.cachedTokenSamples
                  : r.reasoningTokenSamples,
        })),
        {
          key:
            operation === 'PRACTICE_EXTRACTION'
              ? 'aiAnalytics.extractionTokensPerSuccess'
              : 'aiAnalytics.tokensPerPractice',
          value: (r) =>
            f.number(
              r.tokenSamples === r.requests && r.succeeded > 0 && r.totalTokens != null
                ? r.totalTokens / r.succeeded
                : null,
            ),
          samples: (r) => r.tokenSamples,
        },
        {
          key: 'aiAnalytics.cost',
          value: (r) => f.money(r.totalCostUsd),
          samples: (r) => r.costSamples,
        },
        {
          key:
            operation === 'PRACTICE_EXTRACTION'
              ? 'aiAnalytics.extractionCostPerSuccess'
              : 'aiAnalytics.costPerSuccess',
          value: (r) => f.money(r.averageCostPerSuccessUsd),
          samples: (r) => r.costSamples,
        },
        {
          key: 'aiAnalytics.markdownValidity',
          value: (r) => f.rate(r.markdownValidCount, r.qualitySamples),
          samples: (r) => r.qualitySamples,
        },
        {
          key: 'aiAnalytics.questions',
          value: (r) => f.number(r.averageQuestions),
          samples: (r) => r.questionSamples,
        },
        {
          key: 'aiAnalytics.editRate',
          value: (r) => f.rate(r.editedPractices, r.editingSamples),
          samples: (r) => r.editingSamples,
        },
        {
          key: 'aiAnalytics.publicationRate',
          value: (r) => f.rate(r.published, r.succeeded),
          samples: (r) => r.succeeded,
        },
        {
          key: 'aiAnalytics.pipelineCost',
          value: (r) => f.money(r.pipelineCostUsd),
          samples: (r) => r.pipelineCostSamples,
        },
        {
          key: 'aiAnalytics.pipelineLatency',
          value: (r) => f.latency(r.averagePipelineMs),
          samples: (r) => r.pipelineLatencySamples,
        },
      ]
  return (
    <DataTable
      caption={t(
        quality ? 'aiAnalytics.quality' : total ? 'aiAnalytics.overview' : 'aiAnalytics.comparison',
      )}
      rows={
        operation === 'PRACTICE_EXTRACTION'
          ? metrics.filter(
              (m) =>
                !(
                  [
                    'aiAnalytics.markdownValidity',
                    'aiAnalytics.questions',
                    'aiAnalytics.editRate',
                    'aiAnalytics.publicationRate',
                    'aiAnalytics.pipelineCost',
                    'aiAnalytics.pipelineLatency',
                  ] as string[]
                ).includes(m.key),
            )
          : metrics
      }
      rowKey={(m) => m.key}
      columns={[
        { id: 'metric', header: t('aiAnalytics.metric'), cell: (m) => t(m.key) },
        ...rows.map((row, i) => ({
          id: `provider-${i}`,
          header: total ? t('aiAnalytics.overview') : providerName(i === 0 ? 'openai' : 'gemini'),
          cell: (m: Metric) =>
            row ? (
              <div className="flex flex-col gap-1 tabular-nums">
                <span>{m.value(row)}</span>
                <span className="text-caption text-text-secondary">
                  {t('aiAnalytics.samples', [f.number(m.samples(row)), f.number(row.requests)])}
                </span>
              </div>
            ) : (
              t('aiAnalytics.unavailable')
            ),
        })),
      ]}
    />
  )
}
function AnalyticsFilters({
  search,
  change,
}: {
  search: AIAnalyticsSearch
  change: (s: AIAnalyticsSearch) => void
}) {
  const t = useTranslate()
  const defaults = analyticsDates()
  const [draft, setDraft] = useState({
    ...search,
    from: search.from ?? defaults.from,
    to: search.to ?? defaults.to,
    model: search.model ?? '',
  })
  const [version, setVersion] = useState(search.version === undefined ? '' : String(search.version))
  const [invalid, setInvalid] = useState(false)
  const focus = useFocusFirstInvalid<HTMLFormElement>()
  const valid =
    validDay(draft.from) &&
    validDay(draft.to) &&
    Date.parse(draft.to) >= Date.parse(draft.from) &&
    Date.parse(draft.to) - Date.parse(draft.from) < 366 * 86400000
  return (
    <form
      ref={focus.ref}
      noValidate
      onSubmit={(e) => {
        e.preventDefault()
        setInvalid(true)
        if (!valid) {
          focus.request()
          return
        }
        const next = { ...draft, page: 1 }
        delete next.version
        if (version.trim() && Number.isInteger(Number(version)) && Number(version) >= 0)
          next.version = Number(version)
        change(next)
      }}
      className="flex flex-col gap-4"
    >
      <FilterBar density="compact" clearLabel={t('common.clearFilters')} onClear={() => change({})}>
        <TextInput
          label={t('aiAnalytics.from')}
          value={draft.from}
          onChange={(e) => setDraft({ ...draft, from: e.target.value })}
          error={invalid && !valid ? t('aiAnalytics.invalidDates') : ''}
          required
        />
        <TextInput
          label={t('aiAnalytics.to')}
          value={draft.to}
          onChange={(e) => setDraft({ ...draft, to: e.target.value })}
          error={invalid && !valid ? t('aiAnalytics.invalidDates') : ''}
          required
        />
        <Select
          label={t('aiAnalytics.provider')}
          value={draft.provider || 'all'}
          onValueChange={(v) => setDraft({ ...draft, provider: v === 'all' ? '' : v })}
          options={[
            { value: 'all', label: t('aiAnalytics.all') },
            { value: 'openai', label: 'OpenAI' },
            { value: 'gemini', label: 'Gemini' },
          ]}
        />
        <Select
          label={t('aiAnalytics.operation')}
          value={draft.operation ?? 'PRACTICE_GENERATION'}
          onValueChange={(v) =>
            setDraft({ ...draft, operation: v as NonNullable<AIAnalyticsSearch['operation']> })
          }
          options={[
            { value: 'PRACTICE_GENERATION', label: t('aiAnalytics.generation') },
            { value: 'PRACTICE_EXTRACTION', label: t('aiAnalytics.extraction') },
          ]}
        />
        <TextInput
          optional
          label={t('aiAnalytics.model')}
          value={draft.model}
          onChange={(e) => setDraft({ ...draft, model: e.target.value })}
        />
        <TextInput
          label={t('aiAnalytics.version')}
          type="number"
          min={0}
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          optional
        />
        <Select
          label={t('aiAnalytics.status')}
          value={draft.status ?? 'all'}
          onValueChange={(v) => {
            const next = { ...draft }
            delete next.status
            if (v !== 'all') next.status = v as NonNullable<AIAnalyticsSearch['status']>
            setDraft(next)
          }}
          options={[
            { value: 'all', label: t('aiAnalytics.all') },
            ...['RUNNING', 'SUCCEEDED', 'FAILED', 'ABANDONED'].map((v) => ({
              value: v,
              label: aiStatus(t, v),
            })),
          ]}
        />
        <Select
          label={t('aiAnalytics.allocation')}
          value={draft.assignment ?? 'all'}
          onValueChange={(v) => {
            const next = { ...draft }
            delete next.assignment
            if (v !== 'all') next.assignment = v as NonNullable<AIAnalyticsSearch['assignment']>
            setDraft(next)
          }}
          options={[
            { value: 'all', label: t('aiAnalytics.all') },
            { value: 'EXPERIMENT', label: t('aiAnalytics.experimentMode') },
            { value: 'OVERRIDE', label: t('aiAnalytics.overrideMode') },
            { value: 'DEFAULT', label: t('aiAnalytics.defaultMode') },
          ]}
        />
      </FilterBar>
      <Button type="submit" className="self-start">
        {t('aiAnalytics.applyFilters')}
      </Button>
    </form>
  )
}
function DiagnosticDetails({ row }: { row: Diagnostic }) {
  const t = useTranslate()
  const language = useLanguage()
  const f = aiFormatting(language, t)
  return (
    <details className="mt-2">
      <summary className={`cursor-pointer rounded-sm text-body-sm text-primary ${FOCUS_RING}`}>
        {t('aiAnalytics.details')}
      </summary>
      <dl className="flex flex-col gap-2 break-words py-3 text-caption">
        <dt>{t('aiAnalytics.version')}</dt>
        <dd>
          {row.experimentId} · {row.experimentVersion}
        </dd>
        <dt>{t('aiAnalytics.pipelineCost')}</dt>
        <dd>{f.money(row.pipelineCostUsd)}</dd>
        <dt>{t('aiAnalytics.pipelineLatency')}</dt>
        <dd>{f.latency(row.pipelineDurationMs)}</dd>
        <dt>{t('aiAnalytics.executedProvider')}</dt>
        <dd>{row.attempts.map((a) => `${providerName(a.provider)} · ${a.model}`).join(', ')}</dd>
        {row.attempts.map((a) => (
          <div key={a.id}>
            <dt>{t('aiAnalytics.cost')}</dt>
            <dd>{f.money(a.totalCostUsd)}</dd>
            <dt>{t('aiAnalytics.cachedTokens')}</dt>
            <dd>{f.number(a.cachedInputTokens)}</dd>
            <dt>{t('aiAnalytics.reasoningTokens')}</dt>
            <dd>{f.number(a.reasoningTokens)}</dd>
            <dt>{t('aiAnalytics.priceVersion')}</dt>
            <dd>{a.pricingVersion ?? t('aiAnalytics.unavailable')}</dd>
          </div>
        ))}
      </dl>
    </details>
  )
}
